import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Button,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Icon } from "@iconify/react";

// Field definitions for CSV mapping
const memberFields = [
  { key: "title", label: "Anrede", required: false },
  { key: "firstName", label: "Vorname", required: true },
  { key: "lastName", label: "Nachname", required: true },
  { key: "birthdate", label: "Geburtstag", required: false },
  { key: "contractDate", label: "Vertragsdatum", required: false },
  { key: "terminationDate", label: "Kündigungsdatum", required: false },
  { key: "beginningDate", label: "Aktiv seit", required: false },
  { key: "lastTesting", label: "Letzte Prüfung", required: false },
  { key: "degree", label: "Gürtelgrad", required: false },
  { key: "memberNumber", label: "Mitgliedsnummer", required: false },
  { key: "status", label: "Status", required: false },
  { key: "email", label: "E-Mail", required: false },
  { key: "phone", label: "Telefon", required: false },
  { key: "mobile", label: "Mobil", required: false },
  { key: "street", label: "Straße", required: false },
  { key: "zip", label: "PLZ", required: false },
  { key: "city", label: "Ort", required: false },
];

const contractFields = [
  { key: "owner", label: "Kontoinhaber", required: false },
  {
    key: "contractTitle",
    label: "Anrede (Vertragsnehmer/Erziehungsberechtigter)",
    required: false,
  },
  {
    key: "contractFirstName",
    label: "Vorname (Vertragsnehmer/Erziehungsberechtigter)",
    required: false,
  },
  {
    key: "contractLastName",
    label: "Nachname (Vertragsnehmer/Erziehungsberechtigter)",
    required: false,
  },
  { key: "iban", label: "IBAN", required: false },
  { key: "bic", label: "BIC", required: false },
  { key: "bank", label: "Bank", required: false },
];

function MapDataFields({ parsedData, sourceType, detectedTypes, onMapped }) {
  const [mapping, setMapping] = useState({
    members: {},
    contracts: {},
  });
  const [autoMapped, setAutoMapped] = useState(false);

  const autoMapFields = useCallback(() => {
    const headers = parsedData.headers || [];
    const newMapping = {
      members: {},
      contracts: {},
    };

    // Auto-map member fields
    memberFields.forEach((field) => {
      const match = headers.find(
        (h) =>
          h.toLowerCase().includes(field.label.toLowerCase()) ||
          h.toLowerCase().includes(field.key.toLowerCase()),
      );
      if (match) {
        newMapping.members[field.key] = match;
      }
    });

    // Auto-map contract fields if detected
    if (detectedTypes.hasContracts) {
      contractFields.forEach((field) => {
        const match = headers.find(
          (h) =>
            h.toLowerCase().includes(field.label.toLowerCase()) ||
            h.toLowerCase().includes(field.key.toLowerCase()),
        );
        if (match) {
          newMapping.contracts[field.key] = match;
        }
      });
    }

    setMapping(newMapping);
    setAutoMapped(true);
  }, [parsedData, detectedTypes]);

  useEffect(() => {
    // Auto-map fields based on column names
    if (sourceType === "csv" && parsedData?.headers) {
      autoMapFields();
    }
  }, [autoMapFields, parsedData, sourceType]);

  const handleJsonContinue = () => {
    const members = Array.isArray(parsedData?.mitglieder)
      ? parsedData.mitglieder
      : [];
    onMapped({
      autoMapped: true,
      sourceType: "json",
      jsonData: parsedData, // Pass full JSON for later use
      transformedMembers: members, // Pass extracted members
    });
  };

  const handleFieldChange = (entity, fieldKey, value) => {
    setMapping((prev) => ({
      ...prev,
      [entity]: {
        ...prev[entity],
        [fieldKey]: value,
      },
    }));
  };

  const handleContinue = () => {
    onMapped(mapping);
  };

  const availableColumns = parsedData?.headers || [];
  const mappedCount = Object.values(mapping.members).filter(Boolean).length;
  const totalFields = memberFields.filter((f) => f.required).length;

  if (sourceType === "json") {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Icon icon="mdi:check-circle" width={64} style={{ color: "#2e7d32" }} />
        <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
          JSON-Struktur erkannt
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Ihre JSON-Datei enthält bereits die richtige Struktur. Keine manuelle
          Zuordnung erforderlich.
        </Typography>
        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleJsonContinue}
            endIcon={<Icon icon="mdi:arrow-right" />}
          >
            Weiter
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        textAlign="center"
      >
        Felder zuordnen
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        textAlign="center"
        paragraph
      >
        Ordnen Sie die Spalten aus Ihrer CSV-Datei den entsprechenden Feldern zu
      </Typography>

      {autoMapped && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Automatische Zuordnung:</strong> {mappedCount} von{" "}
            {memberFields.length} Feldern wurden automatisch zugeordnet.
            Überprüfen und vervollständigen Sie die Zuordnung unten.
          </Typography>
        </Alert>
      )}

      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Icon icon="mdi:account-group" width={24} />
            <Typography variant="h6">Mitgliederdaten</Typography>
            <Chip
              label={`${
                Object.values(mapping.members).filter(Boolean).length
              }/${memberFields.length}`}
              size="small"
              color="primary"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {memberFields.map((field) => (
              <Grid item xs={12} md={6} key={field.key}>
                <FormControl fullWidth size="small">
                  <InputLabel>
                    {field.label}
                    {field.required && " *"}
                  </InputLabel>
                  <Select
                    value={mapping.members[field.key] || ""}
                    onChange={(e) =>
                      handleFieldChange("members", field.key, e.target.value)
                    }
                    label={`${field.label}${field.required ? " *" : ""}`}
                  >
                    <MenuItem value="">
                      <em>Nicht zuordnen</em>
                    </MenuItem>
                    {availableColumns.map((col) => (
                      <MenuItem key={col} value={col}>
                        {col}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {detectedTypes.hasContracts && (
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <Icon icon="mdi:file-document" width={24} />
              <Typography variant="h6">Vertragsdaten</Typography>
              <Chip
                label={`${
                  Object.values(mapping.contracts).filter(Boolean).length
                }/${contractFields.length}`}
                size="small"
                color="secondary"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {contractFields.map((field) => (
                <Grid item xs={12} md={6} key={field.key}>
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      {field.label}
                      {field.required && " *"}
                    </InputLabel>
                    <Select
                      value={mapping.contracts[field.key] || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "contracts",
                          field.key,
                          e.target.value,
                        )
                      }
                      label={`${field.label}${field.required ? " *" : ""}`}
                    >
                      <MenuItem value="">
                        <em>Nicht zuordnen</em>
                      </MenuItem>
                      {availableColumns.map((col) => (
                        <MenuItem key={col} value={col}>
                          {col}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Hinweis:</strong> Felder mit * sind erforderlich. Stellen Sie
          sicher, dass mindestens Vorname und Nachname zugeordnet sind.
        </Typography>
      </Alert>

      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={mappedCount < totalFields}
          endIcon={<Icon icon="mdi:arrow-right" />}
        >
          Zuordnung übernehmen
        </Button>
      </Box>
    </Box>
  );
}

MapDataFields.propTypes = {
  parsedData: PropTypes.object.isRequired,
  sourceType: PropTypes.string.isRequired,
  detectedTypes: PropTypes.object.isRequired,
  onMapped: PropTypes.func.isRequired,
};

export default MapDataFields;
